import { ParameterResolver } from './ParameterResolver.js';
import { getSeededConfig } from './SeededConfig.js';
import { INFLUENCE_WEIGHTS } from './constants.js';

// Helper to determine section for a given beat
const getContextAt = (beat, structure) => {
    if (!structure || structure.length === 0) return { type: 'default', barIndex: 0, timeSig: 4 };

    let currentBeat = 0;
    let currentBar = 0;

    for (const section of structure) {
        const beatsPerBar = section.timeSig || 4;
        const bars = section.bars || 4;
        const duration = bars * beatsPerBar;

        if (beat >= currentBeat && beat < currentBeat + duration) {
            const relativeBeat = beat - currentBeat;
            const barsIntoSection = Math.floor(relativeBeat / beatsPerBar);

            return {
                type: section.type,
                barIndex: currentBar + barsIntoSection,
                timeSig: beatsPerBar,
                section
            };
        }
        currentBeat += duration;
        currentBar += bars;
    }

    return { type: 'default', barIndex: currentBar, timeSig: 4 };
};

/**
 * Applies dynamic velocity curves based on a named strategy.
 * Used to humanize and shape the mix.
 *
 * Strategies:
 * - 'dynamic_swell': Narratively aware swells (Intro/Outro soft, Chorus loud)
 * - 'terraced': Block dynamics (Baroque style)
 * - 'random_spike': Chaotic accents
 * - 'crescendo': Linear build
 * - 'ghost_heavy': Emphasizes ghost notes
 * - 'jingle': Accents sub-beats
 */
export const applyVelocityStrategy = (events, strategyName, context) => {
    if (!Array.isArray(events)) return events || [];
    if (events.length === 0) return events;

    // Cache resolved parameters per bar to avoid jitter but ensure evolution
    const barParams = {};

    return events.map(ev => {
        if (typeof ev.s !== 'number' || isNaN(ev.s)) return ev;

        // Determine Context for this specific event time
        const eventContext = getContextAt(ev.s, context.structure);
        const { barIndex, type: sectionType, timeSig: beatsPerBar } = eventContext;

        // Extract tension if available (provided by stepOrchestration loop)
        const tension = ev.tension || 0.5;

        // Resolve Parameters for this Bar if not cached
        if (barParams[barIndex] === undefined) {
            const normalizedSectionType = sectionType ? sectionType.toLowerCase() : 'default';

            const resolver = new ParameterResolver(context.random);
            const config = context.config || getSeededConfig('default', 12345);

            // Build Candidates for Base Velocity
            const candidates = [
                // Local/Genre Context (From SeededConfig)
                { value: config.velocities.base, weight: INFLUENCE_WEIGHTS.LOCAL }
            ];

            // Global Context (Section Type - Narrative)
            if (normalizedSectionType.includes('intro') || normalizedSectionType.includes('outro') || normalizedSectionType.includes('break')) {
                candidates.push({ value: 0.35, weight: INFLUENCE_WEIGHTS.NARRATIVE + 30 });
            } else if (normalizedSectionType.includes('chorus') || normalizedSectionType.includes('drop') || normalizedSectionType.includes('climax')) {
                candidates.push({ value: 0.95, weight: INFLUENCE_WEIGHTS.NARRATIVE + 30 });
            }

            // Trait/Strategy Context
            if (strategyName === 'heavy' || strategyName === 'metal' || strategyName === 'gothic') {
                candidates.push({ value: 0.9, weight: INFLUENCE_WEIGHTS.TRAIT });
            } else if (strategyName === 'soft_mist' || strategyName === 'ambient') {
                candidates.push({ value: 0.4, weight: INFLUENCE_WEIGHTS.TRAIT });
            } else if (strategyName === 'terraced') {
                candidates.push({ value: 0.6, weight: INFLUENCE_WEIGHTS.TRAIT });
            }

            // Resolve Base Velocity (Continuous)
            barParams[barIndex] = {
                baseVelocity: resolver.resolveContinuous(candidates, 0.05),
                config: config,
                barIndex: barIndex,
                timbralMotion: (config.vectors ? config.vectors.timbralMotion : 0.5)
            };
        }

        const { baseVelocity, config, timbralMotion } = barParams[barIndex];
        const normalizedSectionType = sectionType ? sectionType.toLowerCase() : 'default';

        let vel = baseVelocity;
        const beat = ev.s % beatsPerBar;
        const subBeat = ev.s % 1;

        // Tension-Velocity Coupling
        if (tension > 0.7) {
            const role = ev.role || 'unknown';
            if (role.includes('pad') || role.includes('strings') || role.includes('harmony')) {
                vel -= 0.1;
            }
            else if (role.includes('perc') || role.includes('piano') || role.includes('guitar')) {
                vel += 0.05;
            }
        }

        // Timbral Motion Scaling
        const motionMult = 0.5 + timbralMotion;

        if (beat < 0.1) {
            vel += (config.velocities.accent * motionMult);
        }
        else if (beatsPerBar === 4) {
            if (Math.abs(beat - 2.0) < 0.1) {
                vel += (config.velocities.accent * 0.6 * motionMult);
            }
        }
        else if (subBeat > 0.4 && subBeat < 0.6) {
            vel -= (0.02 * motionMult);
        }

        if (ev.role && ev.role.includes('kick')) vel += 0.05;
        if (ev.role && ev.role.includes('snare') && (Math.abs(beat - 1) < 0.1 || Math.abs(beat - 3) < 0.1)) vel += 0.05;

        switch (strategyName) {
            case 'random_spike':
                if (Math.random() < 0.2) {
                    vel = 0.5 + (Math.random() * 0.5);
                }
                break;
            case 'dynamic_swell':
                const curve = (context.traits && context.traits.dynamicCurve) || 'swell';
                let swell = 0;
                // Terraced Override
                if (strategyName === 'terraced' || curve === 'stepwise') {
                     if (normalizedSectionType.includes('intro') || normalizedSectionType.includes('outro')) vel = 0.15;
                     else if (normalizedSectionType.includes('a') || normalizedSectionType.includes('verse')) vel = 0.6;
                     else if (normalizedSectionType.includes('b') || normalizedSectionType.includes('chorus')) vel = 1.0;
                     return { ...ev, vel };
                }

                if (curve === 'terrace') {
                     const step = Math.floor(barIndex / 4) % 2;
                     swell = step === 0 ? 0.3 : 0.8;
                } else {
                     const phase = barIndex * 0.5;
                     const rawSine = (Math.sin(phase) + 1) / 2;
                     swell = Math.pow(rawSine, 1.5);
                }
                vel += (swell * 0.3) - 0.15;
                break;
            case 'crescendo':
                const loopPos = barIndex % 4;
                const progress = (loopPos + (beat/4)) / 4;
                vel += (Math.pow(progress, 2) * 0.15) - 0.05;
                break;
            case 'ghost_heavy':
                if (ev.ghost) vel -= 0.2;
                break;
            case 'jingle':
                if (subBeat < 0.1) vel += 0.1;
                break;
            default:
                break;
        }

        vel = Math.max(0.5, Math.min(0.8, vel));
        vel = Math.round(vel * 100) / 100;

        return { ...ev, vel };
    });
};
